import {
  developmentSettings,
  productionSettings,
  stageSettings,
  stagingSettings,
  testserverSettings,
} from './b2c'
import { getDomainSettings } from './domain-settings'
import { remoteBackend } from './remote-backend'

type RemoteBackendTarget = {
  backend: 'stage' | 'dev' | 'local'
  region: 'CZ' | 'PL'
  websiteOrigin: string
  apiProxyOrigin: string
}

const isDev =
  process.env.ENV === 'development' || process.env.ENV === 'integration'

const COMMON = {
  MOCK_API: false,
}
const PRODUCTION = {
  ...COMMON,
  ...productionSettings,
  RUNTIME: 'production' as const,
}
const STAGINGSERVER = {
  ...COMMON,
  ...stagingSettings,
  RUNTIME: 'staging' as const,
}
const STAGESERVER = {
  ...COMMON,
  ...stageSettings,
  RUNTIME: 'stage' as const,
}
const TESTSERVER = {
  ...COMMON,
  ...testserverSettings,
  RUNTIME: 'testserver' as const,
}
const DEVELOP = {
  ...COMMON,
  ...developmentSettings,
  RUNTIME: 'development' as const,
}

type Runtime =
  | 'production'
  | 'testserver'
  | 'integration'
  | 'development'
  | 'staging'
  | 'stage'

export type FirebaseConfig = {
  apiKey: string
  authDomain: string
  databaseURL: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId: string
}

export type EnvConfig = {
  region: GLOBALS.Region
  RUNTIME: Runtime
  MOCK_API: boolean
  /** Fixed deployment region takes precedence over request and DevTools settings. */
  REGION_LOCKED?: boolean
  /** Separates persisted authentication for independently selected backends. */
  AUTH_STORAGE_NAMESPACE?: string
  HOST: string
  BASE_DOMAIN: string
  STATIC_DOMAIN: string
  EXPERIMENT_API_ENDPOINT: `${string}${typeof API_PATHS.experiment}`
  FEDERATED_GRAPHQL_ENDPOINT: `${string}${typeof API_PATHS.federatedGraphql}`
  FILE_UPLOAD_ENDPOINT: `${string}${typeof API_PATHS.fileUpload}`
  PAYINN_API_ENDPOINT: `${string}${typeof API_PATHS.payinn}`
  PSP_API_ENDPOINT: `${string}${typeof API_PATHS.psp}`
  FLOW_SESSION: `${string}${typeof API_PATHS.flowSession}`
  MERCHANT_PORTAL_API_ENDPOINT: `${string}${typeof API_PATHS.merchantPortal}`
  TWISTOJS_API_ENDPOINT: `${string}${typeof API_PATHS.twistoJS}`
  TWISTOJS_DOMAIN: string
  MARQETA_CARD_WIDGET_JS: string
  OCEAN_CARD_WIDGET_JS: string
  MERCHANT_SMI_API_ENDPOINT: `${string}${typeof API_PATHS.merchantSMIApi}`
  TWISTO_JS_SCRIPT_URL_V2: `${string}${typeof API_PATHS.twistoJsScriptV2}`
  TWISTO_JS_SCRIPT_URL_V1: `${string}${typeof API_PATHS.twistoJsScriptV1}`
  ADDITIONAL_DATA_API_ENDPOINT: string
  DAKTELA_GUI_SERVER: string
  FIREBASE_CONFIG?: FirebaseConfig
}

const apiSubdomain = 'api.'
const protocol = 'http://'
const secureProtocol = 'https://'

export const API_PATHS = {
  experiment: '/abtest/experiment/',
  // new hive federated API endpoint for customer and flow
  federatedGraphql: '/customer/v6/graphql/',
  // Endpoint for file upload within customer app
  fileUpload: '/customer/v6/file-upload/',
  // Public installments configuration (max tenor, calculator etc.)
  installmentsConfig: '/installments/config/',
  merchantPortal: '/psp/portal',
  // used for demo-eshop when calling merchant API
  merchantSMIApi: '/psp/smi',
  payinn: '/payinn',
  psp: '/psp/gateway/v1',
  flowSession: '/flow/session/',
  twistoJS: '/v2.5/',
  twistoJsScriptV2: '/v2/lib/twisto.js',
  twistoJsScriptV1: '/v1/lib/twisto.js',
} as const

const getApiUrls = (apiEndpoint: string) =>
  ({
    FEDERATED_GRAPHQL_ENDPOINT: `${apiEndpoint}${API_PATHS.federatedGraphql}`,
    FILE_UPLOAD_ENDPOINT: `${apiEndpoint}${API_PATHS.fileUpload}`,
    PAYINN_API_ENDPOINT: `${apiEndpoint}${API_PATHS.payinn}`,
    PSP_API_ENDPOINT: `${apiEndpoint}${API_PATHS.psp}`,
    MERCHANT_PORTAL_API_ENDPOINT: `${apiEndpoint}${API_PATHS.merchantPortal}`,
    TWISTOJS_API_ENDPOINT: `${apiEndpoint}${API_PATHS.twistoJS}`,
    MERCHANT_SMI_API_ENDPOINT: `${apiEndpoint}${API_PATHS.merchantSMIApi}`,
    TWISTO_JS_SCRIPT_URL_V2: `${apiEndpoint}${API_PATHS.twistoJsScriptV2}`,
    TWISTO_JS_SCRIPT_URL_V1: `${apiEndpoint}${API_PATHS.twistoJsScriptV1}`,
    ADDITIONAL_DATA_API_ENDPOINT: apiEndpoint,
  }) as const

const getUrls = ({
  apiEndpoint,
  environment,
  host,
  protocolPrefix = secureProtocol,
  staticDomain,
  iframeDomain,
}: {
  apiEndpoint: string
  environment: Runtime
  host: string
  protocolPrefix?: typeof secureProtocol | typeof protocol
  staticDomain: string
  iframeDomain?: string
}) => {
  let experimentEndpoint = `${apiEndpoint}${API_PATHS.experiment}` as const
  let daktelaUrl = 'https://twisto.daktela.com/'

  const twistoJSDomain = iframeDomain ?? `${protocolPrefix}${host}`

  // For development and teservers environment, we use the production API
  if (environment !== 'production') {
    experimentEndpoint = `https://api.twisto.cz${API_PATHS.experiment}`
    daktelaUrl = 'https://test-twisto.daktela.com/'
  }

  return {
    HOST: host,
    BASE_DOMAIN: `${protocolPrefix}${host}`,
    STATIC_DOMAIN: staticDomain,
    EXPERIMENT_API_ENDPOINT: experimentEndpoint,
    ...getApiUrls(apiEndpoint),
    FLOW_SESSION: `${protocolPrefix}${host}${API_PATHS.flowSession}`,
    TWISTOJS_DOMAIN: twistoJSDomain,
    DAKTELA_GUI_SERVER: daktelaUrl,
  } as const
}

const getHostEnvConfig = (hostArg?: string): EnvConfig => {
  // dummy values for server rendering
  if (typeof window === 'undefined' && typeof hostArg === 'undefined') {
    return {
      ...DEVELOP,
      MOCK_API: isDev,
      ...getUrls({
        apiEndpoint: '',
        environment: 'development',
        host: '',
        staticDomain: '',
      }),
      region: 'CZ',
      ADDITIONAL_DATA_API_ENDPOINT: 'http://api.cz.twisto.top:8000/',
    }
  }

  const { environment, baseDomain, host, region } = getDomainSettings(
    hostArg ?? window.location.host
  )

  /** localhost */
  if (environment === 'development' || region === null) {
    const { MOCK_PORT } = developmentSettings
    const isLiteralLoopback =
      baseDomain.includes('localhost') || baseDomain.startsWith('192.')
    // A bare remote hostname with no port (e.g. cz.twisto.wtf, the
    // persistent dev cluster) falls into this branch too, since it matches
    // none of the explicit domain patterns above. Unlike real localhost/mock
    // dev, it's served over HTTPS and has no local mock backend to talk to.
    const isMockedDevHost = isLiteralLoopback || baseDomain.includes(':')
    const mockUrl = new URL(`${protocol}${baseDomain}`)
    if (isMockedDevHost) {
      mockUrl.port = MOCK_PORT.toString()
    }
    const apiHostname = isLiteralLoopback ? mockUrl.host : `api.${mockUrl.host}`
    const apiProtocol = isMockedDevHost ? protocol : secureProtocol
    const apiEndpoint = `${apiProtocol}${apiHostname}`

    return {
      ...DEVELOP,
      ...getUrls({
        apiEndpoint,
        environment,
        host,
        protocolPrefix: apiProtocol,
        staticDomain: apiProtocol + host,
      }),
      region: region ?? 'CZ',
      MOCK_API: isMockedDevHost,
      // MOCKING for testing in psp
      FLOW_SESSION: `${protocol}localhost:${MOCK_PORT}${API_PATHS.flowSession}`,
    }
  }

  /** production */
  if (environment === 'production') {
    const apiEndpoint = `${secureProtocol}${apiSubdomain}${baseDomain}`
    const staticDomain = 'https://static.twistopay.com'
    const iframeDomain = `${secureProtocol}www.${baseDomain}`

    return {
      region,
      ...PRODUCTION,
      ...getUrls({
        apiEndpoint,
        environment,
        host,
        staticDomain,
        iframeDomain,
      }),
    }
  }

  /** staging */
  if (environment === 'staging') {
    const apiEndpoint = `${secureProtocol}${apiSubdomain}${baseDomain}`
    const staticDomain = `${secureProtocol}static.${baseDomain.replace(
      /^\w\w\./,
      ''
    )}`
    const iframeDomain = `${secureProtocol}www.${baseDomain}`

    return {
      region,
      ...STAGINGSERVER,
      ...getUrls({
        apiEndpoint,
        environment,
        host,
        staticDomain,
        iframeDomain,
      }),
    }
  }

  /** stage */
  if (environment === 'stage') {
    const apiEndpoint = `${secureProtocol}${apiSubdomain}${baseDomain}`
    const staticDomain = `${secureProtocol}static.${baseDomain.replace(
      /^\w\w\./,
      ''
    )}`
    const iframeDomain = `${secureProtocol}www.${baseDomain}`

    return {
      region,
      ...STAGESERVER,
      ...getUrls({
        apiEndpoint,
        environment,
        host,
        staticDomain,
        iframeDomain,
      }),
    }
  }

  /** testserver */
  if (environment === 'testserver') {
    const apiEndpoint = `${secureProtocol}${apiSubdomain}${baseDomain}`
    const staticDomain = `${secureProtocol}static.${baseDomain.replace(
      /^\w\w\./,
      ''
    )}`
    const iframeDomain = `${secureProtocol}www.${baseDomain}`

    return {
      region,
      ...TESTSERVER,
      ...getUrls({
        apiEndpoint,
        environment,
        host,
        staticDomain,
        iframeDomain,
      }),
    }
  }

  /** integration devspace */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (environment === 'integration') {
    const apiEndpoint = `${protocol}${apiSubdomain}${baseDomain}`
    const staticDomain = `${protocol}${baseDomain.replace(/^\w\w\./, '')}`
    const iframeDomain = `${protocol}www.${baseDomain}`

    return {
      ...DEVELOP,
      region: region.toUpperCase() as GLOBALS.Region,
      ...getUrls({
        apiEndpoint,
        environment,
        host,
        staticDomain,
        iframeDomain,
      }),
      BASE_DOMAIN: `${protocol}${host}`,
    }
  }

  throw new Error('environment not recognized')
}

export const getRemoteEnvConfig = (target: RemoteBackendTarget): EnvConfig => {
  const config = getHostEnvConfig(new URL(target.websiteOrigin).host)
  const api = target.apiProxyOrigin

  return {
    ...config,
    region: target.region,
    MOCK_API: false,
    REGION_LOCKED: true,
    AUTH_STORAGE_NAMESPACE: `${target.backend}_${target.region}`,
    ...getApiUrls(api),
    FLOW_SESSION: `${target.websiteOrigin}${API_PATHS.flowSession}`,
    // Remote dev uses the sandbox widget, not the local mock script.
    MARQETA_CARD_WIDGET_JS: stageSettings.MARQETA_CARD_WIDGET_JS,
  }
}

export const getEnvConfig = (hostArg?: string): EnvConfig => {
  if (remoteBackend == null) {
    return getHostEnvConfig(hostArg)
  }

  return getRemoteEnvConfig(remoteBackend)
}

export const envConfig = getEnvConfig()
